"use strict";

// Copy of Athens shipping-calculator.js. Changes: prices use the shop's money format,
// the shopper's market is preselected, and the postcode survives the footer re-render
// that follows every cart change (the re-render also clears now-stale rates), and it
// is a <div> with Enter handling instead of a <form>. Also defines nc-shipping-estimate.

// Shop money format, e.g. "{{amount_no_decimals_with_comma_separator}} kr" -> "125 kr".
function ncFormatMoney(amount, locale) {
  const format = String(window.shippingCalculator.moneyFormat).replace(/<[^>]*>/g, "");
  const digits = format.indexOf("no_decimals") > -1 ? 0 : 2;
  const number = new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
  return format.replace(/\{\{\s*\w+\s*\}\}/, number);
}

// "Forventet fragt" without a postcode field: fetches the rates once per cart contents
// (cached in sessionStorage, so page views don't hit the carrier again) and shows the
// cheapest. Hidden if no rate comes back.
if (!customElements.get("nc-shipping-estimate")) {
  customElements.define(
    "nc-shipping-estimate",
    class ShippingEstimate extends HTMLElement {
      connectedCallback() {
        const { country, zip, cartKey } = this.dataset;
        const cacheKey = `nc-shipping-estimate:${country}:${zip}:${cartKey}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) return this.show(JSON.parse(cached));

        const query = new URLSearchParams({
          "shipping_address[country]": country,
          "shipping_address[zip]": zip,
        });
        fetch(`${window.routes.cart_url}/shipping_rates.json?${query.toString()}`)
          .then((response) => (response.ok ? response.json() : Promise.reject()))
          .then((json) => {
            const prices = (json.shipping_rates || []).map((rate) => parseFloat(rate.price));
            sessionStorage.setItem(cacheKey, JSON.stringify(prices));
            this.show(prices);
          })
          .catch(() => this.show([]));
      }

      show(prices) {
        if (prices.length === 0) {
          this.hidden = true;
          this.updateTotal(null);
          return;
        }
        const min = Math.min(...prices);
        const single = new Set(prices).size === 1;
        this.updateTotal(single ? min : null);
        const label = this.querySelector(".nc-shipping-estimate__price");
        // One rate at 0 kr = free delivery: say so instead of "0 kr".
        this.classList.toggle("is-free", single && min === 0 && !!this.dataset.freeLabel);
        if (this.classList.contains("is-free")) {
          label.textContent = this.dataset.freeLabel;
          return;
        }
        const price = ncFormatMoney(min, this.dataset.locale);
        label.textContent = single ? price : this.dataset.fromTemplate.replace("[price]", price);
      }

      // The total below (cart drawer / cart page) is the cart total without shipping. One known shipping
      // price > 0: add it and say "Total inkl. fragt". Unknown/several prices: note says "ekskl. fragt".
      // Free: unchanged. Recomputed from data-nc-total, so running twice is harmless.
      updateTotal(shipping) {
        const box = this.closest("#header-mini-cart-footer, #main-cart-footer");
        const total = box && box.querySelector("[data-nc-total]");
        if (!total) return;
        if (shipping > 0) {
          const label = box.querySelector("[data-nc-total-label]");
          total.textContent = ncFormatMoney(Number(total.dataset.ncTotal) / 100 + shipping, this.dataset.locale);
          if (label && label.dataset.inclShipping) label.textContent = label.dataset.inclShipping;
        } else if (shipping === null) {
          const note = box.querySelector("[data-nc-total-note]");
          if (note && note.dataset.exclShipping) note.textContent = note.dataset.exclShipping;
        }
      }
    },
  );
}

if (!customElements.get("nc-shipping-calculator")) {
  customElements.define(
    "nc-shipping-calculator",
    class ShippingCalculator extends HTMLElement {
      constructor() {
        super();

        this.country = this.querySelector(".country");
        this.province = this.querySelector(".province");
        this.postcode = this.querySelector(".postcode");
        this.button = this.querySelector(".button");
        this.rates = this.querySelector(".shipping-rates");
        this.errors = this.querySelector(".errors");

        this.sortCountries();
        this.selectDefaultCountry();

        this.country.addEventListener("change", this.onCountryChange.bind(this));
        this.button.addEventListener("click", this.onSubmit.bind(this));
        // No <form> here (the mini cart is already one), so handle Enter ourselves.
        this.postcode.addEventListener("keydown", (event) => {
          if (event.key === "Enter") this.onSubmit(event);
        });

        // Trigger change to handle showing the Province dropdown if necessary.
        this.country.dispatchEvent(new Event("change"));

        const savedPostcode = sessionStorage.getItem("nc-shipping-postcode");
        if (savedPostcode && !this.postcode.value) this.postcode.value = savedPostcode;
      }

      // Option text is localized the same way as localization.country.name.
      selectDefaultCountry() {
        const option = Array.from(this.country.options).find(
          (o) => o.textContent.trim() === this.dataset.defaultCountry,
        );
        if (option) this.country.value = option.value;
      }


      onSubmit(event) {
        event.preventDefault();
        sessionStorage.setItem("nc-shipping-postcode", this.postcode.value);

        this.classList.add("loading");
        this.rates.classList.add("hidden");
        this.rates.innerHTML = "";

        this.errors.classList.add("hidden");
        this.errors.innerHTML = "";

        const errors = [];

        const query = new URLSearchParams({
          "shipping_address[country]": this.country.value,
          "shipping_address[province]": this.province?.value,
          "shipping_address[zip]": this.postcode.value,
        });

        fetch(`${window.routes.cart_url}/shipping_rates.json?${query.toString()}`)
          .then((data) => data.json())
          .then((json) => {
            if (!json) {
              errors.push(window.shippingCalculator.fetchError);
            }

            json.country && json.country.forEach((error) => errors.push(error));
            json.province && json.province.forEach((error) => errors.push(error));
            json.zip && json.zip.forEach((error) => errors.push(error));

            if (errors.length > 0) {
              // Do nothing. We just want to skip the other checks.
            } else if (json.shipping_rates && json.shipping_rates.length > 0) {
              let html = "";

              json.shipping_rates.forEach((rate) => {
                // Something like "[name]: [symbol][amount] [currency]"
                let shipping = window.shippingCalculator.shippingRateTemplate;
                if (rate.description) {
                  // Something like "[name]: [symbol][amount] [currency] ([description])"
                  shipping = window.shippingCalculator.shippingRateWithDescriptionTemplate;
                }

                let symbol = "";
                if (
                  rate.currency === window.shippingCalculator.currencyCode &&
                  window.shippingCalculator.currencySymbol
                ) {
                  symbol = window.shippingCalculator.currencySymbol;
                }

                let amount = rate.price;
                let currency = rate.currency;
                if (rate.currency === window.shippingCalculator.currencyCode && window.shippingCalculator.moneyFormat) {
                  // The money format already carries the symbol/currency ("125 kr").
                  amount = ncFormatMoney(rate.price, this.dataset.locale);
                  symbol = "";
                  currency = "";
                }

                shipping = shipping.replaceAll("[name]", rate.presentment_name);
                shipping = shipping.replaceAll("[symbol]", symbol);
                shipping = shipping.replaceAll("[amount]", amount);
                shipping = shipping.replaceAll("[currency]", currency).trim();
                shipping = shipping.replaceAll("[description]", rate.description);

                html += `<li>${shipping}</li>`;
              });
              html = `<ul>${html}</ul>`;
              this.rates.append(this.createElement(html));
              this.rates.classList.remove("hidden");
            } else {
              errors.push(window.shippingCalculator.noRatesAvailable);
            }
          })
          .catch(() => {
            errors.push(window.shippingCalculator.responseError);
          })
          .finally(() => {
            this.classList.remove("loading");

            if (errors.length > 0) {
              let html = "";
              errors.forEach((error) => {
                html += `<li>${error}</li>`;
              });
              html = `<ul>${html}</ul>`;

              this.errors.append(this.createElement(html));
              this.errors.classList.remove("hidden");
            }
          });
      }

      onCountryChange(event) {
        const provinces = JSON.parse(
          this.country.querySelector(`option[value="${event.target.value}"]`).dataset.provinces,
        );
        this.province.innerHTML = "";

        if (provinces && provinces.length > 0) {
          let newHtml = "";
          provinces.forEach(([value, label]) => {
            newHtml += `<option value="${value}">${label}</option>`;
          });

          const newElements = Array.from(this.createElements(newHtml));
          newElements.forEach((element) => {
            this.province.append(element);
          });

          this.enableProvince();
        } else {
          this.disableProvince();
        }
      }

      enableProvince() {
        this.province.disabled = false;
        this.province.parentElement.classList.remove("hidden");
      }

      disableProvince() {
        this.province.disabled = true;
        this.province.parentElement.classList.add("hidden");
      }

      sortCountries() {
        const options = Array.from(this.country.children);
        const separatorIndex = options.findIndex((option) => option.value === "---");
        const firstOption = options[0];

        let optionsBeforeSeparator = [];
        let optionsAfterSeparator = [];
        let separatorElement = null;

        if (separatorIndex >= -1) {
          separatorElement = options[separatorIndex];
          optionsBeforeSeparator = options.slice(0, separatorIndex);
          optionsAfterSeparator = options.slice(separatorIndex + 1);
        }

        if (separatorElement) {
          // Disable the default (usually) "---" separator  option.
          separatorElement.disabled = true;
        }

        // Sort countries alphabetically
        const comparator = new Intl.Collator().compare;
        optionsBeforeSeparator.sort((a, b) => {
          return comparator(a.textContent, b.textContent);
        });
        optionsAfterSeparator.sort((a, b) => {
          return comparator(a.textContent, b.textContent);
        });

        this.country.innerHTML = "";

        if (optionsBeforeSeparator.length > 0) {
          optionsBeforeSeparator.forEach((elem) => this.country.append(elem));
        }
        if (separatorElement) {
          this.country.append(separatorElement);
        }
        if (optionsAfterSeparator.length > 0) {
          optionsAfterSeparator.forEach((elem) => this.country.append(elem));
        }

        this.country.value = firstOption.getAttribute("value");
      }

      createElements(stringHtml) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(stringHtml, "text/html");
        return doc.body.children;
      }

      createElement(stringHtml) {
        return this.createElements(stringHtml)[0];
      }
    },
  );
}
